/**
 * OAuth Popup Timeout Handler
 *
 * Wraps Firebase signInWithPopup with a timeout to handle cases where
 * the user closes the OAuth provider's popup and Firebase takes too long
 * to detect the closure.
 *
 * When a user closes the OAuth popup (by clicking X or pressing Escape),
 * the Firebase SDK may take up to 15+ seconds to recognize the popup
 * closure and reject the promise. This utility adds a faster timeout
 * to detect popup closure and immediately reject.
 *
 * @see RFC: https://github.com/i-clique/retireEZ2/issues/XXX
 */

import type { Auth, AuthProvider, UserCredential } from "firebase/auth";
import { signInWithPopup as firebaseSignInWithPopup } from "firebase/auth";

/**
 * Timeout for OAuth popup completion (in milliseconds)
 *
 * This is set to 10 seconds to catch most popup closures before
 * Firebase's internal timeout. Most users complete OAuth within 5-8 seconds,
 * so 10 seconds provides a reasonable buffer while still catching early closures.
 *
 * If a legitimate OAuth flow takes longer than this, the user can retry.
 */
const OAUTH_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Wrap Firebase signInWithPopup with a timeout
 *
 * If the OAuth flow doesn't complete within the timeout, reject with
 * a "PopupTimeout" error. This provides faster error detection when
 * users close the OAuth provider's popup.
 *
 * @param auth - Firebase Auth instance
 * @param provider - OAuth provider (Google, Apple, Microsoft, etc.)
 * @returns Promise that resolves with UserCredential or rejects on timeout
 *
 * @throws Error with message "OAuth popup closed or timed out"
 */
export async function signInWithPopupWithTimeout(
  auth: Auth,
  provider: AuthProvider
): Promise<UserCredential> {
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let completed = false;

    // Create the Firebase promise
    const oauthPromise = firebaseSignInWithPopup(auth, provider);

    // Set up timeout handler
    timeoutId = setTimeout(() => {
      if (!completed) {
        completed = true;
        const error = new Error("OAuth popup was closed or timed out");
        Object.defineProperty(error, "code", {
          value: "auth/popup-timeout",
          enumerable: true,
        });
        reject(error);
      }
    }, OAUTH_TIMEOUT_MS);

    // Handle Firebase result
    oauthPromise
      .then((result) => {
        if (!completed) {
          completed = true;
          if (timeoutId) clearTimeout(timeoutId);
          resolve(result);
        }
      })
      .catch((error) => {
        if (!completed) {
          completed = true;
          if (timeoutId) clearTimeout(timeoutId);
          reject(error);
        }
      });
  });
}
